import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Sparkles, Loader2, Send, Trash2, Mail, FileText, Receipt } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "@/components/studio/StudioUI";
import { studio, apiError, inputCls, btnPrimary, btnGhost, beDate } from "@/lib/studioApi";

const rel = (iso) => { const d = Math.round((Date.now() - new Date(iso)) / 36e5); return d < 1 ? "à l'instant" : d < 24 ? `il y a ${d} h` : `il y a ${Math.round(d / 24)} j`; };

export const MessageCard = ({ m, onChange, onDelete, compact = false, autoOpen = false }) => {
  const [draft, setDraft] = useState({ subject: m.reply_subject || "", body: m.reply_body || "", to: m.client_email || "" });
  const [busy, setBusy] = useState("");
  const [openReply, setOpenReply] = useState(false);
  const answered = m.status === "repondue";
  const cardRef = useRef(null);
  const autoRan = useRef(false);

  const generate = async (silent = false) => {
    setBusy("draft");
    try { const { data } = await studio.post(`/messages/${m.id}/draft`, {}, { timeout: 90000 }); setDraft((d) => ({ ...d, ...data })); setOpenReply(true); toast.success(silent ? "Réponse rédigée par l'IA : relisez puis envoyez." : "Brouillon rédigé : relisez puis envoyez."); }
    catch (e) { toast.error(apiError(e, "Rédaction impossible.")); } finally { setBusy(""); }
  };

  useEffect(() => {
    if (!autoOpen || autoRan.current) return;
    autoRan.current = true;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (answered) return;
    if (m.reply_body) setOpenReply(true); else generate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);
  const send = async () => {
    setBusy("send");
    try { const { data } = await studio.post(`/messages/${m.id}/reply`, draft); onChange(data); setOpenReply(false); toast.success(`Réponse envoyée à ${data.client_email}.`); }
    catch (e) { toast.error(apiError(e, "Envoi impossible.")); } finally { setBusy(""); }
  };
  const docLink = m.doc_id ? (m.doc_type === "devis" ? `/studio/devis/${m.doc_id}` : `/studio/factures/${m.doc_id}`) : "";
  const DocIcon = m.doc_type === "devis" ? FileText : Receipt;

  return (
    <div ref={cardRef} data-testid={`message-${m.id}`} className={`rounded-2xl border bg-white p-4 sm:p-5 ${answered ? "border-[#E2E7F7]" : "border-[#C9D4F4]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-heading text-sm font-extrabold text-[#0F2645]">{m.client_name || "Client"} <span className="ml-1 text-xs font-semibold text-[#93A0BC]">{rel(m.created_at)} · {m.source === "web" ? "depuis la page du document" : "collée par vous"}</span></p>
          <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#5C6A87]">
            {m.doc_ref && !compact && (docLink ? <Link to={docLink} data-testid={`message-doc-${m.id}`} className="inline-flex items-center gap-1 font-semibold text-[#3A5CC8] hover:underline"><DocIcon className="h-3 w-3" />{m.doc_ref}</Link> : <span className="inline-flex items-center gap-1"><DocIcon className="h-3 w-3" />{m.doc_ref}</span>)}
            {m.client_email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{m.client_email}</span>}
          </p>
        </div>
        <span data-testid={`message-status-${m.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${answered ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>{answered ? `Répondue le ${beDate(m.replied_at)}` : "À répondre"}</span>
      </div>
      <blockquote className="mt-3 whitespace-pre-wrap rounded-xl border-l-4 border-[#C9D4F4] bg-[#F6F8FD] px-4 py-3 text-sm leading-relaxed text-[#0F2645]">{m.question}</blockquote>

      {openReply && (
        <div data-testid={`message-reply-form-${m.id}`} className="mt-4 space-y-3">
          <input data-testid={`message-reply-to-${m.id}`} type="email" className={inputCls} placeholder="E-mail du client *" value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))} />
          <input data-testid={`message-reply-subject-${m.id}`} className={inputCls} placeholder="Objet" value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} />
          <textarea data-testid={`message-reply-body-${m.id}`} rows={9} className={inputCls} value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
          <p className="text-[11px] text-[#93A0BC]">La réponse part à votre marque (logo, coordonnées) avec un bouton vers le document. Relisez-la : l'IA ne connaît que les données du document.</p>
        </div>
      )}
      {answered && !openReply && m.reply_body && (
        <details data-testid={`message-sent-reply-${m.id}`} className="mt-3 text-xs text-[#5C6A87]"><summary className="cursor-pointer font-semibold text-[#0F2645]">Voir la réponse envoyée</summary><p className="mt-2 whitespace-pre-wrap rounded-xl bg-[#F6F8FD] px-4 py-3 leading-relaxed">{m.reply_body}</p></details>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!answered && !openReply && (
          <button type="button" data-testid={`message-draft-${m.id}`} onClick={() => generate()} disabled={busy === "draft"} className={btnPrimary}>{busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Rédiger la réponse (IA)</button>
        )}
        {openReply && (
          <>
            <button type="button" data-testid={`message-send-${m.id}`} onClick={send} disabled={busy === "send" || !draft.body.trim()} className={btnPrimary}>{busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer au client</button>
            <button type="button" data-testid={`message-regenerate-${m.id}`} onClick={() => generate()} disabled={busy === "draft"} className={btnGhost}>{busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Reformuler</button>
          </>
        )}
        {!answered && !openReply && <button type="button" data-testid={`message-manual-${m.id}`} onClick={() => setOpenReply(true)} className={btnGhost}>Répondre moi-même</button>}
        {answered && <button type="button" data-testid={`message-reopen-${m.id}`} onClick={() => setOpenReply((o) => !o)} className={btnGhost}>{openReply ? "Fermer" : "Nouvelle réponse"}</button>}
        <button type="button" data-testid={`message-delete-${m.id}`} onClick={() => onDelete(m)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#93A0BC] hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /> Supprimer</button>
      </div>
    </div>
  );
};

export const ClientQuestions = ({ docId, docType }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { if (docId) studio.get(`/messages?doc_id=${docId}`).then(({ data }) => setItems(data.items)).catch(() => setItems([])); }, [docId]);
  if (!items?.length) return null;
  const open = items.filter((m) => m.status === "nouvelle").length;
  const remove = async (m) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try { await studio.delete(`/messages/${m.id}`); setItems((l) => l.filter((x) => x.id !== m.id)); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <SectionCard
      testId="doc-questions"
      className="mt-4"
      title={<span className="inline-flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#3A5CC8]" /> Questions du client</span>}
      hint={open ? `${open} question${open > 1 ? "s" : ""} en attente de réponse` : `${items.length} question${items.length > 1 ? "s" : ""}, toutes répondues`}
      action={<Link to="/studio/messages" data-testid="doc-questions-all" className="text-xs font-semibold text-[#3A5CC8] hover:underline">Toutes les questions</Link>}
    >
      <div className="space-y-3">
        {items.map((m) => <MessageCard key={m.id} m={m} compact onChange={(msg) => setItems((l) => l.map((x) => (x.id === msg.id ? { ...x, ...msg } : x)))} onDelete={remove} />)}
      </div>
    </SectionCard>
  );
};
